import React, { useEffect, useState } from "react";
import localStyles from "./index.module.css";
import commonStyles from '../common/commonStyles/table.module.css';
import { Appointment } from "../dashboard";
import {Button, Form, Header, Icon, Loader, Menu, Message, Modal, Table} from "semantic-ui-react";
import agent, { PaginateOptions } from "../../agent";
import { Link } from "react-router-dom";
import { hasRole, UserRole } from "../../helpers/UserRole";
import { GeneralTitleCase, getGeneralTitle } from "../../utils/common";
import TablePage from "../general/TablePage";
import SafeHTMLRenderer from "../general/SafeHTMLRenderer";
import { useUrlPagination } from "../../hooks/useUrlPagination";
import {
    CommonListPagePaginationResponse,
    DEFAULT_LIMIT,
    DEFAULT_PAGE,
    DEFAULT_SORT_COLUMN,
    handleSortPagination,
    ListState,
} from "../../helpers/TablePageUtils";
import { textLabels } from "../../consts/textLabels";

export interface AppointmentPaginationResponse extends CommonListPagePaginationResponse<Appointment> {}

const initialState: ListState<Appointment> = {
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
    sortColumn: DEFAULT_SORT_COLUMN,
    searchValue: "",
    docs: [],
    totalDocs: 0,
    totalPages: 1,
    loading: false,
    error: null,
};

const AppointmentList: React.FC = () => {
    const currentPath = "/dashboard/appointments";

    const { state, setState, updateUrl } = useUrlPagination<ListState<Appointment>>(initialState, {
        defaultSortColumn: DEFAULT_SORT_COLUMN,
    });

    const [searchInput, setSearchInput] = useState(state.searchValue);
    const [openRemoveModal, setOpenRemoveModal] = useState(false);
    const [idToRemove, setIdToRemove] = useState<string | null>(null);

    const fetchList = async () => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        const paginateOptions: PaginateOptions = {
            page: state.page,
            limit: state.limit,
            sort: state.sortColumn,
            search: state.searchValue,
        };

        try {
            const response: AppointmentPaginationResponse = await agent.AppointmentProvider.getList(paginateOptions);

            if (response) {
                setState(prev => ({
                    ...prev,
                    docs: response.docs,
                    totalDocs: response.totalDocs,
                    totalPages: response.totalPages,
                    loading: false,
                    error: null,
                }));
            }
        } catch (error) {
            console.error("Ошибка получения:", error);
            setState(prev => ({
                ...prev,
                docs: [],
                totalDocs: 0,
                totalPages: 1,
                loading: false,
                error: `Не удалось загрузить список ${getGeneralTitle(GeneralTitleCase.appointmentsov)}. Пожалуйста, попробуйте позже.`,
            }));
        }
    };

    useEffect(() => {
        fetchList();
    }, [state.page, state.limit, state.sortColumn, state.searchValue]);

    useEffect(() => {
        updateUrl();
    }, [state.page, state.limit, state.sortColumn, state.searchValue]);

    const handleSearch = () => {
        setState(prev => ({ ...prev, searchValue: searchInput, page: DEFAULT_PAGE }));
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setState(prev => ({ ...prev, searchValue: "", page: DEFAULT_PAGE }));
    };

    const handleSort = (column: string) => {
        const newSort = handleSortPagination(state.sortColumn, column);
        setState(prev => ({ ...prev, sortColumn: newSort, page: DEFAULT_PAGE }));
    };

    const handlePageChange = (page: number) => {
        setState(prev => ({ ...prev, page }));
    };

    const handleLimitChange = (limit: number) => {
        setState(prev => ({ ...prev, limit, page: DEFAULT_PAGE }));
    };

    const handleSearchInputChange = (value: string) => {
        setSearchInput(value);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const openRemoveModalHandler = (id: string) => {
        setIdToRemove(id);
        setOpenRemoveModal(true);
    };

    const closeRemoveModal = () => {
        setOpenRemoveModal(false);
        setIdToRemove(null);
    };

    const removeAppointment = async () => {
        if (!idToRemove) return;

        setState(prev => ({ ...prev, loading: true }));

        try {
            await agent.AppointmentProvider.remove(idToRemove);
            fetchList();
        } catch (error) {
            console.error("Ошибка удаления:", error);
            setState(prev => ({
                ...prev,
                error: `Не удалось удалить ${getGeneralTitle(GeneralTitleCase.appointmentsim)}. Пожалуйста, попробуйте позже.`,
            }));
        } finally {
            closeRemoveModal();
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    const {
        docs,
        totalDocs,
        totalPages,
        loading,
        error,
        page,
        limit,
        sortColumn,
    } = state;
    const styles = { ...commonStyles, ...localStyles };
    const headerLabel = `Список ${getGeneralTitle(GeneralTitleCase.appointmentsov)}`;
    const confirmRemoveLabel = `Вы уверены, что хотите удалить ${getGeneralTitle(GeneralTitleCase.appointmentsim)}?`;
    const removeLabel = textLabels.deleteButton.toUpperCase();
    const cancelLabel = textLabels.cancelButton;
    const createLabel = textLabels.createButton;
    const listLoaderLabel = textLabels.listLoaderMessage;
    const errorLabel = textLabels.errorMessage;

    return (
        <div className={styles.table}>
            <Modal
                open={openRemoveModal}
                onClose={closeRemoveModal}
                closeIcon
            >
                <Modal.Header>{confirmRemoveLabel}</Modal.Header>
                <Modal.Actions>
                    <Button content={removeLabel} onClick={removeAppointment} positive />
                    <Button color="grey" onClick={closeRemoveModal}>
                        {cancelLabel}
                    </Button>
                </Modal.Actions>
            </Modal>

            <div className={styles.header}>
                <Header>{headerLabel}</Header>
                {hasRole(UserRole.ADMIN) && (
                    <Link to={`${currentPath}/create`}>
                        <Button
                            className={styles.createButton}
                            size="large"
                            color="green"
                            type="submit"
                        >
                            {createLabel}
                        </Button>
                    </Link>
                )}
            </div>

            {error && (
                <Message negative>
                    <Message.Header>{errorLabel}</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            {loading ? (
                <Loader active>{listLoaderLabel}</Loader>
            ) : (
                <div className={styles.list}>
                    <Form>
                        <Form.Group>
                            <Form.Input
                                placeholder="Поиск"
                                iconPosition="left"
                                icon={<Icon name="search" />}
                                value={searchInput}
                                onChange={(e) => handleSearchInputChange(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                            <Form.Button primary content="Поиск" onClick={handleSearch} />
                            <Form.Button basic color="orange" content="Сбросить" onClick={handleClearSearch} />
                        </Form.Group>
                    </Form>

                    <div className={styles.container}>
                        <Table celled selectable striped verticalAlign="middle" color="teal">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "name" || sortColumn === "-name"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("name")}
                                    >
                                        Наименование {getGeneralTitle(GeneralTitleCase.appointmentsa)}
                                    </Table.HeaderCell>
                                    {hasRole(UserRole.ADMIN) && <Table.HeaderCell />}
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {docs.map((el) => (
                                    <Table.Row style={{ cursor: "pointer" }} key={`appointment-${el._id}`}>
                                        <Table.Cell floated="left">
                                            {hasRole(UserRole.ADMIN) ? (
                                                <Link
                                                    className={styles.nameColumn}
                                                    to={{
                                                        pathname: `${currentPath}/${el._id}/edit`,
                                                        search: `?returnUrl=${encodeURIComponent(window.location.href)}`,
                                                    }}
                                                >
                                                    <SafeHTMLRenderer html={el.name} />
                                                </Link>
                                            ) : (
                                                <SafeHTMLRenderer html={el.name} />
                                            )}
                                        </Table.Cell>

                                        {hasRole(UserRole.ADMIN) && (
                                            <Table.Cell floated="right" style={{ width: "5%" }}>
                                                <Button
                                                    icon
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openRemoveModalHandler(el._id);
                                                    }}
                                                >
                                                    <Icon color="red" name="trash alternate outline" />
                                                </Button>
                                            </Table.Cell>
                                        )}
                                    </Table.Row>
                                ))}
                            </Table.Body>

                            <Table.Footer>
                                <Table.Row>
                                    <Table.HeaderCell colSpan={hasRole(UserRole.ADMIN) ? 2 : 1}>
                                        <Menu floated="right" pagination>
                                            <TablePage
                                                currentPage={page}
                                                totalPages={totalPages}
                                                totalDocs={totalDocs}
                                                limit={limit}
                                                onPageChange={handlePageChange}
                                                onLimitChange={handleLimitChange}
                                            />
                                        </Menu>
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Footer>
                        </Table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AppointmentList;